// ============================================================
// E 组 /m：营销中台 H5 API 客户端
// - 独立 JWT（与 dtwin 主站登录态无关，localStorage: dtwince.m.*）
// - 开发直连 marketing-api:3100；生产走 api.dtwince.com（CORS 白名单已含 dtwince.cloud）
// - 401 自动清理登录态并回 /m/login
// ============================================================

import { create } from 'zustand';

const env = (import.meta as unknown as { env?: Record<string, string | boolean | undefined> }).env ?? {};

export const MARKETING_API_BASE =
  (env.VITE_MARKETING_API_BASE as string | undefined) ||
  (env.DEV ? 'http://localhost:3100' : 'https://api.dtwince.com');

const TOKEN_KEY = 'dtwince.m.token';
const USER_KEY = 'dtwince.m.user';

export interface MUser {
  id: number;
  username: string;
  roleCode: string;
  deptId?: number | null;
}

function readUser(): MUser | null {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
  } catch {
    return null;
  }
}

interface MAuthState {
  token: string;
  user: MUser | null;
  setAuth: (token: string, user: MUser) => void;
  clear: () => void;
}

export const useMAuth = create<MAuthState>((set) => ({
  token: localStorage.getItem(TOKEN_KEY) || '',
  user: readUser(),
  setAuth: (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    set({ token, user });
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    set({ token: '', user: null });
  },
}));

export class MApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string | number,
    public hint?: string,
  ) {
    super(message);
    this.name = 'MApiError';
  }
}

export async function mFetch<T>(path: string, init?: RequestInit & { auth?: boolean }): Promise<T> {
  const auth = init?.auth !== false;
  const token = useMAuth.getState().token;
  const res = await fetch(`${MARKETING_API_BASE}/api${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(auth && token ? { authorization: `Bearer ${token}` } : {}),
      ...((init?.headers as Record<string, string>) || {}),
    },
  });
  if (res.status === 401 && auth) {
    useMAuth.getState().clear();
    if (!window.location.pathname.startsWith('/m/login')) window.location.replace('/m/login');
    throw new MApiError(401, '登录已过期，请重新登录');
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const d = data as { message?: string | string[]; code?: string | number; hint?: string } | null;
    const raw = d?.message;
    const message = Array.isArray(raw) ? raw[0] : raw || `请求失败（${res.status}）`;
    throw new MApiError(res.status, String(message), d?.code, d?.hint);
  }
  return data as T;
}

// ── 微信网页授权 ──

/** 取网页授权地址（mock 模式会直接回跳 /m/callback?code=mock-*） */
export async function fetchMpOauthUrl(redirectUri: string): Promise<{ url: string; mock: boolean }> {
  return mFetch(`/wechat-mp/oauth/url?redirect=${encodeURIComponent(redirectUri)}`, { auth: false });
}

/** 发起微信授权登录（整页跳转） */
export async function startMpOauth(): Promise<void> {
  const redirect = `${window.location.origin}/m/callback`;
  const r = await fetchMpOauthUrl(redirect);
  window.location.href = r.url;
}

export interface MCallbackResult {
  bound: boolean;
  token?: string;
  user?: MUser;
  ticket?: string;
  binding?: { openidMasked: string; nickname?: string | null };
}

/** 网页授权回调（code → 登录态 / bind ticket） */
export async function mpOauthCallback(code: string, state?: string): Promise<MCallbackResult> {
  return mFetch('/wechat-mp/oauth/callback', {
    method: 'POST',
    auth: false,
    body: JSON.stringify({ code, state }),
  });
}

export const BIND_TICKET_KEY = 'dtwince.m.bindTicket';
